import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { useContent } from "../lib/content";

const FRAME_COLORS = ["#eac119", "#808bc5", "#eaa7c7", "#9ed6df", "#ed773c", "#245e55"];

const fullUrl = (u) =>
  u && u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`;

export const GalleryMosaic = () => {
  const { t } = useContent();
  const [photos, setPhotos] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api
      .get("/gallery")
      .then(({ data }) => {
        if (Array.isArray(data)) setPhotos(data.map((p) => fullUrl(p.url)));
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  return (
    <section id="gallery" className="relative bg-[#1d1d1b] py-20 sm:py-24 overflow-hidden">
      <div className="max-w-6xl mx-auto px-6">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-[11px] font-bold tracking-[0.3em] uppercase text-[#eae4da]/50 flex items-center gap-3"
        >
          <span className="w-8 h-px bg-[#eac119]" />
          {t("gallery_kicker")}
        </motion.p>

        {loaded && photos.length === 0 ? (
          <p className="mt-10 font-editorial text-2xl text-[#eae4da]/60">
            Photos from our first nights are coming soon.
          </p>
        ) : (
          <div data-testid="gallery-grid" className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            {photos.slice(0, 8).map((src, i) => {
              const big = i % 5 === 0;
              return (
                <motion.div
                  key={src + i}
                  data-testid={`gallery-tile-${i}`}
                  initial={{ opacity: 0, y: 30, scale: 0.97 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.6, delay: (i % 4) * 0.08, ease: [0.22, 1, 0.36, 1] }}
                  className={`${big ? "col-span-2 row-span-2" : ""} rounded-2xl p-2 sm:p-2.5 transition-transform duration-500 hover:scale-[1.02] hover:-rotate-1`}
                  style={{ backgroundColor: FRAME_COLORS[i % FRAME_COLORS.length] }}
                >
                  <img
                    src={src}
                    alt={`MAS Arts gallery photo ${i + 1}`}
                    loading="lazy"
                    className={`w-full object-cover rounded-xl ${big ? "h-full min-h-[220px]" : "h-36 sm:h-44"}`}
                  />
                </motion.div>
              );
            })}
          </div>
        )}

        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="font-display font-extrabold tracking-[-0.03em] text-white text-5xl sm:text-7xl mt-10"
        >
          {t("gallery_heading")}
          <span className="text-[#eac119]">.</span>
        </motion.h2>
      </div>
    </section>
  );
};
